/**
 * RetentionNextMilestoneCard Component
 * 
 * PATCH v2: Slim single-row strip for dashboard
 * Shows: 🔥 streak | progress bar + days to go | +reward min
 */

import React from 'react';
import { Flame, Gift } from 'lucide-react';

const RetentionNextMilestoneCard = ({ 
  streakCurrent = 0,
  nextMilestoneDay = null,
  nextMilestoneRewardMinutes = 0,
  daysRemaining = 0,
  nextClaimableMilestone = null,
  claimableBonusMinutes = 0,
  onClaimClick = null,
}) => {
  const hasClaimable = nextClaimableMilestone !== null && claimableBonusMinutes > 0;
  const progress = nextMilestoneDay 
    ? Math.min(100, ((nextMilestoneDay - daysRemaining) / nextMilestoneDay) * 100)
    : 100;

  // Claimable state - slightly taller but still compact
  if (hasClaimable) {
    return (
      <div 
        className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50"
        data-testid="next-milestone-card"
      >
        <div className="flex items-center gap-2">
          <Gift className="w-4 h-4 text-green-600" />
          <span className="text-sm font-medium text-green-800 dark:text-green-300">
            Day {nextClaimableMilestone} reached!
          </span>
        </div>
        {onClaimClick ? (
          <button
            onClick={onClaimClick}
            className="px-3 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded-md font-medium transition-colors"
            data-testid="claim-milestone-button"
          >
            Claim +{claimableBonusMinutes}min
          </button>
        ) : (
          <span className="text-xs font-semibold text-green-600 dark:text-green-400">
            +{claimableBonusMinutes}min
          </span>
        )}
      </div>
    );
  }

  // Normal state - single row slim strip
  return (
    <div 
      className="flex items-center gap-3 px-3 py-2 rounded-lg bg-purple-50/50 dark:bg-purple-900/10 border border-purple-200/50 dark:border-purple-800/30"
      data-testid="next-milestone-card"
    >
      {/* Left: Streak */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Flame className="w-4 h-4 text-orange-500" />
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{streakCurrent}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">day</span>
      </div>

      {/* Center: Progress bar + days remaining */}
      {nextMilestoneDay ? (
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <div className="flex-1 h-1.5 bg-purple-200 dark:bg-purple-800/50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="milestone-progress-bar"
            />
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
            {daysRemaining}d to go
          </span>
        </div>
      ) : (
        <div className="flex-1 text-xs text-gray-500 dark:text-gray-400">
          Keep going!
        </div>
      )}

      {/* Right: Reward */}
      {nextMilestoneDay && nextMilestoneRewardMinutes > 0 && (
        <span className="text-xs font-medium text-purple-600 dark:text-purple-400 whitespace-nowrap flex-shrink-0">
          +{nextMilestoneRewardMinutes}min
        </span>
      )}
    </div>
  );
};

export default RetentionNextMilestoneCard;
